/**
 * Companion token management API.
 */

import { apiRequest } from "./client";
import type {
  AvailableRuntime,
  AvailableModelsMap,
} from "@/stores/companion-store";

export interface ManagedTrialStatus {
  enabled: boolean;
  cost_used_usd: string;
  cost_cap_usd: string;
  setup_required: boolean;
}

interface CompanionInfo {
  managed_trial?: ManagedTrialStatus;
  has_token: boolean;
  companion_online?: boolean;
  companion_updated_at?: string;
  companion_version?: string;
  default_runtime?: string;
  /** Saved selection; unlike default_runtime it is never replaced when that CLI is missing. */
  preferred_runtime?: string | null;
  available_runtimes?: AvailableRuntime[];
  available_models?: AvailableModelsMap;
}

interface TokenStatusResponse {
  status: string;
  data: CompanionInfo;
}

export async function getTokenStatus(): Promise<CompanionInfo> {
  const res = await apiRequest<TokenStatusResponse>(
    "/api/v1/auth/companion-token/",
    { silent: true },
  );
  return res.data;
}

export type { CompanionInfo };

export async function updateDefaultRuntime(
  runtime: string,
  opencodeModel?: string,
  options: { onboardingRecovery?: boolean } = {},
): Promise<string> {
  const res = await apiRequest<{
    status: string;
    data: { default_runtime: string };
  }>("/api/v1/auth/companion-token/", {
    method: "PATCH",
    body: {
      default_runtime: runtime,
      ...(opencodeModel ? { opencode_model: opencodeModel } : {}),
      ...(options.onboardingRecovery ? { onboarding_recovery: true } : {}),
    },
  });
  return res.data.default_runtime;
}

export async function activateOnboardingRuntimeStarter(): Promise<string> {
  const res = await apiRequest<{
    status: string;
    data: { default_runtime: string };
  }>("/api/v1/auth/onboarding/runtime-starter/", { method: "POST" });
  return res.data.default_runtime;
}
